/**
 * ChoreStatusStepper Component
 * Visual progression indicator for chore status with interactive advancement
 *
 * Usage:
 *   <ChoreStatusStepper
 *     status="completed"
 *     verificationEnabled={true}
 *     isParent={true}
 *     onAdvance={(newStatus) => handleStatusChange(newStatus)}
 *   />
 */
import React, { useState } from 'react'
import { CheckCircle2, Circle, ChevronRight, Loader2 } from 'lucide-react'
import {
  DISPLAY_STEPS,
  getStepIndex,
  canAdvanceStatus,
  StatusStep
} from '../hooks/useChoreStatusTransition'

export interface ChoreStatusStepperProps {
  /** Current assignment status */
  status: string
  /** Whether chore verification is enabled for this family */
  verificationEnabled: boolean
  /** Whether the current user is a parent */
  isParent: boolean
  /** Callback when user advances to next status */
  onAdvance?: (newStatus: string) => void | Promise<void>
  /** Whether the stepper is interactive (allows clicking) */
  interactive?: boolean
  /** Compact mode for inline display */
  compact?: boolean
  /** Show labels below steps */
  showLabels?: boolean
  /** Loading state */
  loading?: boolean
  /** Custom class name */
  className?: string
  /** Show as bonus/open chore (includes Open step) */
  isBonusChore?: boolean
}

export function ChoreStatusStepper({
  status,
  verificationEnabled,
  isParent,
  onAdvance,
  interactive = true,
  compact = false,
  showLabels = true,
  loading = false,
  className = '',
  isBonusChore = false
}: ChoreStatusStepperProps) {
  const [hoverStep, setHoverStep] = useState<number | null>(null)

  // Get steps to display - hide verified step if verification disabled
  const steps = verificationEnabled ? DISPLAY_STEPS : DISPLAY_STEPS.slice(0, -1)
  const currentIndex = getStepIndex(status, verificationEnabled)
  const { canAdvance, nextStatus } = canAdvanceStatus(status, isParent, verificationEnabled)

  const handleStepClick = async (step: StatusStep, stepIndex: number) => {
    if (!interactive || loading || !onAdvance) return

    // Can only click the next step
    if (stepIndex === currentIndex + 1 && canAdvance && nextStatus) {
      await onAdvance(nextStatus)
    }
  }

  const getStepState = (stepIndex: number): 'completed' | 'current' | 'next' | 'future' => {
    if (stepIndex < currentIndex) return 'completed'
    if (stepIndex === currentIndex) return 'current'
    if (stepIndex === currentIndex + 1 && canAdvance) return 'next'
    return 'future'
  }

  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {steps.map((step, index) => {
          const state = getStepState(index)
          const isClickable = interactive && state === 'next' && onAdvance

          return (
            <React.Fragment key={step.value}>
              <button
                type="button"
                disabled={!isClickable || loading}
                onClick={() => handleStepClick(step, index)}
                className={`
                  w-2.5 h-2.5 rounded-full transition-all duration-200
                  ${state === 'completed' ? 'bg-green-500' : ''}
                  ${state === 'current' ? step.bgColor : ''}
                  ${state === 'next' ? `bg-gray-300 ${isClickable ? 'hover:bg-gray-400 cursor-pointer ring-2 ring-gray-200 animate-pulse' : ''}` : ''}
                  ${state === 'future' ? 'bg-gray-200' : ''}
                `}
                title={step.label}
              />
              {index < steps.length - 1 && (
                <div className={`w-2 h-0.5 ${index < currentIndex ? 'bg-green-400' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  return (
    <div className={`flex items-center ${className}`}>
      {steps.map((step, index) => {
        const state = getStepState(index)
        const isClickable = interactive && state === 'next' && onAdvance
        const isHovered = hoverStep === index

        return (
          <React.Fragment key={step.value}>
            {/* Step Circle & Label */}
            <div className="flex flex-col items-center">
              <button
                type="button"
                disabled={!isClickable || loading}
                onClick={() => handleStepClick(step, index)}
                onMouseEnter={() => isClickable && setHoverStep(index)}
                onMouseLeave={() => setHoverStep(null)}
                className={`
                  relative flex items-center justify-center transition-all duration-200
                  ${compact ? 'w-6 h-6' : 'w-8 h-8'}
                  rounded-full border-2
                  ${state === 'completed'
                    ? 'bg-green-500 border-green-500 text-white'
                    : state === 'current'
                    ? `${step.bgColor} border-transparent text-white`
                    : state === 'next' && isClickable
                    ? `bg-white border-gray-300 text-gray-400 hover:border-gray-400 hover:bg-gray-50 cursor-pointer ${isHovered ? 'ring-4 ring-blue-100' : ''}`
                    : 'bg-gray-100 border-gray-200 text-gray-300'
                  }
                  ${isClickable && !loading ? 'cursor-pointer' : 'cursor-default'}
                `}
                title={isClickable ? `Click to ${step.label.toLowerCase()}` : step.label}
              >
                {loading && state === 'next' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : state === 'completed' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <Circle className="w-4 h-4" fill={state === 'current' ? 'currentColor' : 'none'} />
                )}

                {/* Pulse animation for clickable next step */}
                {isClickable && !loading && (
                  <span className="absolute inset-0 rounded-full animate-ping bg-blue-400 opacity-20" />
                )}
              </button>

              {/* Label */}
              {showLabels && (
                <span className={`
                  mt-1 text-xs font-medium
                  ${state === 'completed' ? 'text-green-600' : ''}
                  ${state === 'current' ? step.textColor : ''}
                  ${state === 'next' ? 'text-gray-500' : ''}
                  ${state === 'future' ? 'text-gray-300' : ''}
                `}>
                  {compact ? step.shortLabel : step.label}
                </span>
              )}
            </div>

            {/* Connector Line */}
            {index < steps.length - 1 && (
              <div className={`
                flex-1 h-0.5 mx-2
                ${index < currentIndex ? 'bg-green-400' : 'bg-gray-200'}
              `}>
                {/* Progress indicator on current step */}
                {index === currentIndex && (
                  <div className={`h-full ${step.bgColor} w-1/2 rounded-r`} />
                )}
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

/**
 * Inline status badge with mini visual stepper
 * Shows visual progression dots with current status highlighted
 * For use in list items where space is limited
 */
export function ChoreStatusBadge({
  status,
  verificationEnabled,
  isParent,
  onAdvance,
  loading = false
}: Omit<ChoreStatusStepperProps, 'compact' | 'showLabels' | 'className' | 'interactive'> & { onAdvance?: (newStatus: string) => void | Promise<void> }) {
  const steps = verificationEnabled ? DISPLAY_STEPS : DISPLAY_STEPS.slice(0, -1)
  const currentIndex = getStepIndex(status, verificationEnabled)
  const currentStep = steps[Math.min(currentIndex, steps.length - 1)]
  const { canAdvance, nextStatus } = canAdvanceStatus(status, isParent, verificationEnabled)

  return (
    <div className="flex items-center gap-3">
      {/* Mini visual stepper with dots and labels */}
      <div className="flex items-center gap-1">
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex
          const isCurrent = index === currentIndex
          const isNext = index === currentIndex + 1 && canAdvance
          const isFuture = index > currentIndex

          return (
            <React.Fragment key={step.value}>
              {/* Step dot with label */}
              <div className="flex flex-col items-center">
                <div
                  className={`
                    w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium
                    ${isCompleted ? 'bg-green-500 text-white' : ''}
                    ${isCurrent ? `${step.bgColor} text-white ring-2 ring-offset-1 ring-${step.color}-300` : ''}
                    ${isNext ? 'bg-gray-200 text-gray-500' : ''}
                    ${isFuture && !isNext ? 'bg-gray-100 text-gray-400' : ''}
                  `}
                  title={step.label}
                >
                  {isCompleted ? '✓' : (index + 1)}
                </div>
                <span className={`
                  text-[9px] mt-0.5 whitespace-nowrap
                  ${isCompleted ? 'text-green-600' : ''}
                  ${isCurrent ? step.textColor + ' font-medium' : ''}
                  ${isFuture ? 'text-gray-400' : ''}
                `}>
                  {step.shortLabel}
                </span>
              </div>
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className={`w-3 h-0.5 mb-4 ${index < currentIndex ? 'bg-green-400' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          )
        })}
      </div>

      {/* Advance button */}
      {canAdvance && nextStatus && onAdvance && (
        <button
          type="button"
          disabled={loading}
          onClick={() => onAdvance(nextStatus)}
          className={`
            inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium
            bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 transition-colors
            ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
          `}
        >
          {loading ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <>
              <ChevronRight className="w-3 h-3" />
              Mark {steps[currentIndex + 1]?.shortLabel}
            </>
          )}
        </button>
      )}

      {/* Waiting for parent indicator for children */}
      {status === 'completed' && !isParent && verificationEnabled && (
        <span className="text-xs text-orange-500 italic">
          Waiting for verification
        </span>
      )}
    </div>
  )
}

export default ChoreStatusStepper
